import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@mui/material";
import { Plus, Edit2, Trash2, Save, Image, Upload } from "lucide-react";
import getSubmitLabel from "@/utils/getSubmitLabel";
import {
  TeamsSettingsTabProps,
  TeamFormPanelProps,
  useTeamsSettingsData,
} from "@/lib/settings/teamsSettingsData";
import { Controller } from "react-hook-form";
import { CharLimitMessage } from "@/components/common/CharLimitMessage";
import { TEAM_LIMITS } from "@/lib/settings/settingsLimits";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import {
  accessTitanverseTeamUpsert,
  accessTitanverseTeamView,
} from "@/data/constantStrings";
import {
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";


// Main Teams Settings Tab Component
export function TeamsSettingsTab({
  onAddTeam,
  onEditTeam,
  confirmDelete,
}: TeamsSettingsTabProps) {
  const {
    isDarkMode,
    teams,
    isTeamsLoading,
    handleGetTeamMembersName,
    handleGetPharmacyNames,
    handleDeleteTeamButton,
  } = useTeamsSettingsData();
const hasTeamPermission = checkUserPermissions(accessTitanverseTeamUpsert);
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2
          className={`text-lg font-semibold transition-colors duration-300 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Teams
        </h2>

        <div
          onClick={() => {
            if (!hasTeamPermission) {
              setShowPermissionMessage("Create or update team");
              setShowPermissionModal(true);
            }
          }}
          className={!hasTeamPermission ? "cursor-not-allowed" : ""}
        >
          <Button
            size="sm"
            className={`bg-slate-900 hover:bg-slate-800 ${
              !hasTeamPermission ? "opacity-50" : ""
            }`}
            onClick={() => {
              if (!hasTeamPermission) return;
              onAddTeam();
            }}
            disabled={!hasTeamPermission}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Team
          </Button>
        </div>
      </div>

      {/* Teams List */}
      <div className="space-y-4">
        {isTeamsLoading
          ? // Skeleton loading state
            [...Array(3)].map((_, index) => (
              <div
                key={`skeleton-${index}`}
                className={`border rounded-lg p-4 transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-700 bg-gray-800"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Skeleton variant="circular" width={16} height={16} />
                      <Skeleton variant="text" width={180} height={24} />
                      <Skeleton variant="rounded" width={60} height={20} />
                    </div>
                    <Skeleton
                      variant="text"
                      width="90%"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton
                      variant="text"
                      width="70%"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="40%" height={20} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton variant="rounded" width={32} height={32} />
                  </div>
                </div>
              </div>
            ))
          : teams.map((team) => (
              <div
                key={team?.teamID}
                className={`border rounded-lg p-4 transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-700 bg-gray-800"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className={`w-4 h-4 rounded-full border transition-colors duration-300 ${
                          isDarkMode ? "border-gray-600" : "border-gray-300"
                        }`}
                        style={{
                          backgroundColor: team?.teamColor ?? "#3b82f6",
                        }}
                      />
                      <h3
                        className={`font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {team?.teamName || "-"}
                      </h3>
                      <Badge variant={team.isActive ? "default" : "secondary"}>
                        {team.isActive ? "ACTIVE" : "INACTIVE"}
                      </Badge>
                    </div>
                    <p
                      className={`text-sm mb-2 transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      {team?.teamDescription || "-"}
                    </p>
                    <div
                      className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      <span className="font-medium">Members:</span>{" "}
                      <span
                        className={
                          team?.teamMembers && team?.teamMembers?.length > 0
                            ? ""
                            : "italic"
                        }
                      >
                        {handleGetTeamMembersName(team?.teamMembers)}
                      </span>
                    </div>
                    <div
                      className={`text-sm mt-1 transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      <span className="font-medium">Pharmacy Access:</span>{" "}
                      <span
                        className={
                          team?.pharmacies && team?.pharmacies.length > 0
                            ? ""
                            : "italic"
                        }
                      >
                        {handleGetPharmacyNames(team?.pharmacies)}
                      </span>
                    </div>
                    <div
                      className={`text-sm mt-1 transition-colors duration-300 ${
                        isDarkMode ? "text-gray-500" : "text-gray-500"
                      }`}
                    >
                      {team?.teamMembers?.length} member
                      {team?.teamMembers?.length !== 1 ? "s" : ""} •{" "}
                      {team?.pharmacies ? team?.pharmacies?.length : 0} pharmacy
                      access
                      {team?.pharmacies && team?.pharmacies?.length !== 1
                        ? "es"
                        : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditTeam(team)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {/* <Button
                  variant="ghost"
                  size="sm"
                  disabled
                  onClick={() =>
                    confirmDelete(
                      () => handleDeleteTeamButton(team?.teamID),
                      deleteConfirmationMessage + "team"
                    )
                  }
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button> */}
                  </div>
                </div>
              </div>
            ))}
      </div>
    </div>
  );
}

// Team Form Panel Component
export function TeamFormPanel({ team, onSave, onBack }: TeamFormPanelProps) {
  const {
    isDarkMode,
    formData,
    allPharmacies,
    membersData,
    logoPreview,
    isTeamLoading,
    errors,
    isValid,
    isDirty,
    control,
    setValue,
    setLogoPreview,
    register,
    watch,
    handleTogglePharmacyInTeam,
    handleToggleMemberInTeam,
    handleLogoUpload,
    onSubmit,
    handleSubmit,
    handleInputChange,
    isFormSubmitLoading,
    LogoError,
    handleRemoveLogo
  } = useTeamsSettingsData({ initialTeam: team, onSave });
const hasTeamPermission = checkUserPermissions(accessTitanverseTeamUpsert);
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
      {/* Header with Back Button */}
      <div
        className={`sticky top-0 z-10 flex items-center gap-3 border-b pb-4 transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={
            isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : ""
          }
        >
          ← Back
        </Button>
        <h2
          className={`text-lg font-semibold transition-colors duration-300 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          {formData?.teamID ? "Edit Team" : "Add New Team"}
        </h2>
      </div>
      <div className="space-y-4">
        <div className="space-y-6">
          <div>
            <h4
              className={`font-medium mb-4 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Team Information
            </h4>
            {/* Team Name */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="team-name">Team Name</Label>
                <Input
                  id="team-name"
                  {...register("teamName", {
                    required: "Team name is required",
                    pattern: {
                      value: /^(?!\d+$)[A-Za-z0-9 ._-]{1,50}$/,
                      message: "Invalid team name format",
                    },
                  })}
                  placeholder="Enter team name"
                  maxLength={TEAM_LIMITS.teamName}
                />
                <CharLimitMessage value={watch("teamName")} max={TEAM_LIMITS.teamName} />
                {errors.teamName && (
                  <p className="text-sm text-red-500">
                    {String(errors.teamName.message)}
                  </p>
                )}
              </div>

              {/* Team Color */}
              <div className="space-y-2">
                <Label htmlFor="team-color">Team Color</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="team-color"
                    type="color"
                    {...register("teamColor", {
                      pattern: {
                        value: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
                        message: "Invalid color format",
                      },
                    })}
                    onChange={(e) =>
                      setValue("teamColor", e.target.value, {
                        shouldDirty: true,
                      })
                    }
                    className="w-16 h-10"
                  />
                  <Input
                    id="team-color"
                    {...register("teamColor")}
                    placeholder="#3b82f6"
                    onChange={(e) =>
                      setValue("teamColor", e.target.value, {
                        shouldDirty: true,
                      })
                    }
                    className="flex-1"
                  />
                </div>
                {errors.teamColor && (
                  <p className="text-sm text-red-500">
                    {String(errors.teamColor.message)}
                  </p>
                )}
              </div>
            </div>

            {/* Team Description */}
            <div className="mt-4 space-y-2">
              <Label htmlFor="team-description">Description</Label>
              <Textarea
                id="team-description"
                {...register("teamDescription")}
                placeholder="Enter team description"
                rows={3}
                maxLength={TEAM_LIMITS.teamDescription}
                className={`transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-600 text-white"
                    : "bg-white"
                }`}
              />
              <CharLimitMessage
                value={watch("teamDescription")}
                max={TEAM_LIMITS.teamDescription}
              />
              {errors.teamDescription && (
                <p className="text-sm text-red-500">
                  {String(errors.teamDescription.message)}
                </p>
              )}
            </div>
            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="company-logo"
                  className={`transition-colors duration-300 ${
                    isDarkMode ? "text-gray-200" : "text-gray-900"
                  }`}
                >
                  Team Logo
                </Label>
                <p
                  className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Upload your team logo (recommended size: 200x80px)
                </p>

                <div className="flex items-center gap-4">
                  {/* Logo Preview */}
                  <div className="flex-shrink-0">
                    <div
                      className={`w-24 h-10 border-2 border-dashed rounded-lg flex items-center justify-center overflow-hidden transition-colors duration-300 ${
                        isDarkMode
                          ? "border-gray-600 bg-gray-800"
                          : "border-gray-300 bg-gray-50"
                      }`}
                    >
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="object-contain w-full h-full"
                        />
                      ) : (
                        <Image
                          className={`w-5 h-5 transition-colors duration-300 ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        />
                      )}
                    </div>
                    <p
                      className={`text-xs mt-1 text-center transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Preview
                    </p>
                  </div>

                  {/* Upload Button */}
                  <div className="flex-1">
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        document.getElementById("logo-upload")?.click()
                      }
                      className={`w-full justify-center transition-colors duration-300 ${
                        isDarkMode
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : ""
                      }`}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {logoPreview ? "Change Logo" : "Upload Logo"}
                    </Button>

                    {logoPreview && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveLogo()}
                        className="w-full mt-2 text-red-600 hover:text-red-700"
                      >
                        Remove Logo
                      </Button>
                    )}

                    {LogoError && (
                      <p className="mt-2 text-sm text-red-600">
                        {LogoError}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center mt-4 space-x-2">
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <Switch
                    id="team-active"
                    checked={field.value}
                    onCheckedChange={(val) => field.onChange(val)}
                  />
                )}
              />
              <Label htmlFor="team-active">Active Team</Label>
            </div>
          </div>

          {/* Team Members */}
          <div>
            <h4
              className={`font-medium mb-4 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Team Members
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {membersData?.map((user, index) => (
                <div
                  key={`${user?.userId}-${index}`}
                  className={`flex items-center space-x-3 p-3 border rounded-lg transition-colors duration-300 ${
                    isDarkMode
                      ? "border-gray-700 hover:bg-gray-700"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    id={`user-${user?.userId}`}
                    checked={(formData?.teamMembers || []).some(
                      (m) => m.email === user?.emailAddress,
                    )}
                    onChange={() =>
                      handleToggleMemberInTeam(user?.emailAddress)
                    }
                    className="rounded"
                  />
                  <div className="flex-1">
                    <label
                      htmlFor={`member-${user?.userId}`}
                      className={`text-sm font-medium cursor-pointer block transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {user?.fullName}
                    </label>
                    <div
                      className={`text-xs transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {user?.emailAddress}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {errors.teamMembers && (
              <p className="text-sm text-red-500">
                {String(errors.teamMembers.message)}
              </p>
            )}
          </div>

          {/* Pharmacy Access */}
          <div>
            <h4
              className={`font-medium mb-4 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Pharmacy Access
            </h4>
            <p
              className={`text-sm mb-4 transition-colors duration-300 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Select which pharmacies this team can access
            </p>
            <div className="grid grid-cols-1 gap-3">
              {allPharmacies?.map((pharmacy, index) => (
                <div
                  key={`${pharmacy?.pharmacyID}-${index}`}
                  className={`flex items-center space-x-3 p-3 border rounded-lg transition-colors duration-300 ${
                    isDarkMode
                      ? "border-gray-700 hover:bg-gray-700"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    id={`pharmacy-${pharmacy?.pharmacyID}`}
                    checked={(formData?.pharmacies || []).some(
                      (p) => p.teamPharmacyID === pharmacy?.pharmacyID,
                    )}
                    onChange={() =>
                      handleTogglePharmacyInTeam(pharmacy?.pharmacyID)
                    }
                    className="rounded"
                  />
                  <div className="flex-1">
                    <label
                      htmlFor={`pharmacy-${pharmacy?.pharmacyID}`}
                      className={`text-sm font-medium cursor-pointer block transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {pharmacy?.pharmacyName || "-"}
                    </label>
                    <div
                      className={`text-xs transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {pharmacy?.pharmacyAddress || "-"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {errors.pharmacies && (
              <p className="text-sm text-red-500">
                {String(errors.pharmacies.message)}
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div
          className={`flex gap-2 justify-end pt-4 border-t transition-colors duration-300 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <Button
            variant="outline"
            disabled={isFormSubmitLoading}
            onClick={onBack}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isFormSubmitLoading || !isValid || !isDirty}
            className={`bg-slate-900 hover:bg-slate-800 ${
              !hasTeamPermission ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={(e) => {
              if (!hasTeamPermission) {
                e.preventDefault(); 
                setShowPermissionMessage("Create or update team");
                setShowPermissionModal(true);
                return;
              }
            }}
          >
            <Save className="w-4 h-4 mr-2" />
            {getSubmitLabel(
              team?.teamID,
              isFormSubmitLoading,
              "Team",
              isFormSubmitLoading,
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
